/**
 * Session Form Component
 * ตามรูปอ้างอิง: Session Info Card, Start Page (ล็อค = last_page + 1), End Page, Session Impact, ปุ่มบันทึก
 *
 * - start_page = หน้าล่าสุดที่บันทึก + 1 (ล็อคไว้ ไม่แก้ได้ ถ้าไม่เปิดโหมดระบุเอง)
 * - Session Impact = end_page - start_page + 1 (คำนวณอัตโนมัติ)
 */

import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { sessionsService } from '@/services/sessions.service';
import { goalsService } from '@/services/goals.service';
import type { CreateSessionRequest } from '@/types/sessions';
import { Lock, Check, History, Minus, Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import { cn } from '@/lib/utils';

interface SessionFormProps {
  open?: boolean;
  onSuccess?: () => void;
}

const MADANI_MAX_PAGE = 604;

function formatSessionDate(date: Date): string {
  return date.toLocaleDateString('th-TH', {
    weekday: 'long',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function SessionForm({ open = false, onSuccess }: SessionFormProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [endPage, setEndPage] = useState<string>('');
  const [useCustomStartPage, setUseCustomStartPage] = useState(false);
  const [customStartPage, setCustomStartPage] = useState<string>('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  // ใช้ progress API เป็นแหล่งข้อมูลหลัก (last_read_page, next_page_to_read, sessions_count_today)
  const { data: progressData } = useQuery({
    queryKey: ['goals', 'current', 'progress'],
    queryFn: () => goalsService.getCurrentGoalProgress(),
    enabled: open,
  });

  // Fallback: ดึง sessions เมื่อไม่มี progress (ไม่มี active goal)
  const { data: sessionsData } = useQuery({
    queryKey: ['sessions', 'list'],
    queryFn: () => sessionsService.list({ limit: 50 }),
    enabled: open,
  });

  // หน้าล่าสุดที่บันทึก + หน้าเริ่มต้น: จาก progress API หรือจาก sessions
  const lastPageRecorded = useMemo(() => {
    if (progressData != null) return progressData.last_read_page;
    const list = sessionsData?.data ?? [];
    const first = list[0];
    return first ? first.end_page : 0;
  }, [progressData, sessionsData?.data]);

  const defaultStartPage = useMemo(() => {
    if (progressData != null) {
      const next = progressData.next_page_to_read;
      return next > MADANI_MAX_PAGE ? 1 : next;
    }
    const next = lastPageRecorded + 1;
    return next > MADANI_MAX_PAGE ? 1 : next;
  }, [progressData, lastPageRecorded]);

  // ตั้ง value หน้าสิ้นสุดเป็น defaultStartPage (lastPageRecorded + 1) เมื่อโหลดข้อมูลเสร็จหรือเมื่อช่องว่าง
  useEffect(() => {
    if (endPage === '' && defaultStartPage >= 1) {
      setEndPage(String(defaultStartPage));
    }
  }, [defaultStartPage, endPage]);
  const startPage = useCustomStartPage
    ? (customStartPage ? parseInt(customStartPage, 10) : 0)
    : defaultStartPage;
  const startPageValid = startPage >= 1 && startPage <= MADANI_MAX_PAGE;

  const endPageNum = endPage ? parseInt(endPage, 10) : 0;
  const endPageValid = !Number.isNaN(endPageNum) && endPageNum >= 1 && endPageNum <= MADANI_MAX_PAGE;

  // Session Impact = จำนวนหน้าที่ได้ใน session นี้ (คำนวณอัตโนมัติ)
  const sessionImpactPages =
    startPageValid && endPageValid
      ? endPageNum >= startPage
        ? endPageNum - startPage + 1
        : (MADANI_MAX_PAGE - startPage + 1) + endPageNum // Wrap around case
      : 0;

  const sessionNumber =
    progressData != null
      ? progressData.sessions_count_today + 1
      : (sessionsData?.data?.length ?? 0) + 1;

  const createMutation = useMutation({
    mutationFn: (data: CreateSessionRequest) => sessionsService.create(data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['sessions'] });
      queryClient.invalidateQueries({ queryKey: ['goals', 'current', 'progress'] });
      setEndPage('');
      setCustomStartPage('');
      setUseCustomStartPage(false);
      setErrors({});
      if (result?.goal_completed && result?.completed_goal_id) {
        navigate(`/goal-completed/${result.completed_goal_id}`, { replace: true });
        return;
      }
      toast.success('บันทึกเซสชันการอ่านเรียบร้อยแล้ว');
      onSuccess?.();
    },
    onError: (error: any) => {
      if (error.code === 'VALIDATION_ERROR') {
        toast.error(
          'ยังบันทึกไม่ได้ — หน้าสิ้นสุดต้องมากกว่าหน้าเริ่มต้น หรือคุณยังอ่านรอบก่อนหน้าไม่ครบ กรุณาตรวจสอบอีกครั้ง',
          { duration: 5000, position: 'top-center' }
        );
        return;
      }
      if (error.details) {
        const next: Record<string, string> = {};
        error.details.forEach((d: { path: string; message: string }) => {
          next[d.path] = d.message;
        });
        setErrors(next);
      } else {
        setErrors({ general: error.message || 'เกิดข้อผิดพลาดในการบันทึก' });
        toast.error(error.message || 'เกิดข้อผิดพลาดในการบันทึก');
      }
    },
  });

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!endPage) {
      next.end_page = 'กรุณาระบุหน้าสิ้นสุด';
    } else if (!endPageValid) {
      next.end_page = `หน้าต้องอยู่ระหว่าง 1-${MADANI_MAX_PAGE}`;
    }
    // Note: We allow endPage < startPage for wrap-around cases (e.g. 600 -> 4)
    
    if (useCustomStartPage) {
      if (!customStartPage) {
        next.start_page = 'กรุณาระบุหน้าเริ่มต้น';
      } else {
        const s = parseInt(customStartPage, 10);
        if (Number.isNaN(s) || s < 1 || s > MADANI_MAX_PAGE) {
          next.start_page = `หน้าต้องอยู่ระหว่าง 1-${MADANI_MAX_PAGE}`;
        }
        // Also allow wrap around in custom start page mode
      }
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const payload: CreateSessionRequest = {
      end_page: endPageNum,
    };
    if (useCustomStartPage && customStartPage) {
      payload.start_page = parseInt(customStartPage, 10);
    }

    createMutation.mutate(payload);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 max-w-md mx-auto">
      {errors.general && (
        <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
          <p className="text-sm text-destructive">{errors.general}</p>
        </div>
      )}

      {/* Session Info Card */}
      <div className="rounded-xl border border-border bg-card p-4 pl-5 border-l-4 border-l-primary">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          {formatSessionDate(new Date())}
        </p>
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          <span className="text-xl font-bold text-foreground">
            เซสชันที่ {sessionNumber}
          </span>
          <Badge variant="secondary" className="bg-primary/20 text-primary">
            ความคืบหน้า Khatm
          </Badge>
        </div>
        <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
          <History className="h-4 w-4 shrink-0" />
          <span>หน้าล่าสุดที่บันทึก: {lastPageRecorded || '-'}</span>
        </div>
      </div>

      {/* Start Page (ล็อค = last_page + 1) */}
      <div className="space-y-2">
        <Label htmlFor="start_page">หน้าเริ่มต้น</Label>
        <div className="relative flex items-center">
          <Input
            id="start_page"
            type="number"
            min={1}
            max={MADANI_MAX_PAGE}
            value={useCustomStartPage ? customStartPage : String(defaultStartPage)}
            onChange={useCustomStartPage ? (e) => setCustomStartPage(e.target.value) : undefined}
            readOnly={!useCustomStartPage}
            className={cn(
              'text-lg pr-10',
              !useCustomStartPage && 'bg-muted cursor-not-allowed'
            )}
            aria-invalid={!!errors.start_page}
          />
          {!useCustomStartPage && (
            <span className="absolute right-3 text-muted-foreground pointer-events-none" aria-hidden>
              <Lock className="h-4 w-4" />
            </span>
          )}
        </div>
        {errors.start_page && (
          <p className="text-sm text-destructive">{errors.start_page}</p>
        )}
        {!useCustomStartPage && (
          <p className="text-xs text-muted-foreground">
            คำนวณจากหน้าล่าสุดที่บันทึก + 1
          </p>
        )}
        <label className="flex items-center gap-2 mt-2 cursor-pointer">
          <input
            type="checkbox"
            checked={useCustomStartPage}
            onChange={(e) => {
              setUseCustomStartPage(e.target.checked);
              if (!e.target.checked) setCustomStartPage('');
            }}
            className="rounded border-input"
          />
          <span className="text-sm text-muted-foreground">ระบุหน้าเริ่มต้นเอง (อ่านไม่ต่อเนื่อง)</span>
        </label>
      </div>

      {/* End Page */}
      <div className="space-y-2 flex flex-col items-center">
        <Label htmlFor="end_page" className="w-full text-center sm:text-left">หน้าสิ้นสุด</Label>
        <div className="flex items-center justify-center gap-3 w-full max-w-[220px] mx-auto">
          <button
            type="button"
            onClick={() => {
              const n = endPage ? parseInt(endPage, 10) : defaultStartPage;
              const next = Math.max(1, (Number.isNaN(n) ? defaultStartPage : n) - 1);
              setEndPage(String(next));
            }}
            className="flex items-center justify-center w-10 h-10 shrink-0 rounded-full border-2 border-input bg-muted hover:bg-muted/80 transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 cursor-pointer disabled:opacity-50"
            aria-label="ลดหน้า"
          >
            <Minus className="h-5 w-5 text-foreground" />
          </button>
          <Input
            id="end_page"
            type="number"
            min={1}
            max={MADANI_MAX_PAGE}
            value={endPage}
            onChange={(e) => setEndPage(e.target.value)}
            placeholder="เช่น 58"
            className={cn(
              'text-lg text-center w-20 h-10 rounded-md border border-input focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
              errors.end_page && 'border-destructive'
            )}
            aria-invalid={!!errors.end_page}
          />
          <button
            type="button"
            onClick={() => {
              const n = endPage ? parseInt(endPage, 10) : defaultStartPage;
              const current = Number.isNaN(n) ? defaultStartPage : n;
              const next = current >= MADANI_MAX_PAGE ? 1 : current + 1;
              setEndPage(String(next));
            }}
            className="flex items-center justify-center w-10 h-10 shrink-0 rounded-full border-2 border-input bg-muted hover:bg-muted/80 transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 cursor-pointer disabled:opacity-50"
            aria-label="เพิ่มหน้า"
          >
            <Plus className="h-5 w-5 text-foreground" />
          </button>
        </div>
        {errors.end_page && (
          <p className="text-sm text-destructive">{errors.end_page}</p>
        )}
      </div>

      {/* Session Impact: จำนวนหน้าที่ได้อัตโนมัติ */}
      <div className="rounded-xl border border-border bg-muted/30 p-5 text-center">
        <p className="text-sm font-medium text-muted-foreground mb-1">ผลของเซสชัน</p>
        <p className="text-4xl font-bold text-primary">
          {sessionImpactPages.toLocaleString('th-TH')} <span className="text-lg font-normal text-foreground">หน้า</span>
        </p>
        <p className="text-sm text-muted-foreground mt-2">
          เพิ่มไปยังความคืบหน้าการอ่านทั้งหมดของคุณ
        </p>
      </div>

      <Button
        type="submit"
        disabled={createMutation.isPending || sessionImpactPages <= 0}
        className="w-full text-base font-semibold py-6"
      >
        {createMutation.isPending ? (
          'กำลังบันทึก...'
        ) : (
          <>
            <Check className="h-5 w-5 mr-2 shrink-0" />
            บันทึกเซสชันการอ่าน
          </>
        )}
      </Button>
    </form>
  );
}

export default SessionForm;
